/**
 * Insight Types for PropAI Insights Feature
 *
 * This is where we add insights
 *
 * This file contains all TypeScript type definitions for the insights system.
 * It includes insight types, severity mappings, and data structures for the API responses.
 */

import { logger } from "@core/logger";

/**
 * Severity classification for insights
 * - "good": Positive insights indicating good performance
 * - "bad": Issues that need attention or improvements
 */
export type InsightSeverity = "good" | "bad";

/**
 * Add a new entry here when the API adds a new insight type.
 * - type: internal InsightType
 * - apiEnum: exact string the API sends (insight_type)
 * - severity: good | bad
 */
const INSIGHT_TYPE_DEFINITIONS = [
  {
    type: "too_cold_sensor",
    apiEnum: "INSIGHT_TYPE_ENUM_SENSOR_TOO_COLD",
    severity: "bad",
  },
  {
    type: "too_warm_sensor",
    apiEnum: "INSIGHT_TYPE_ENUM_SENSOR_TOO_WARM",
    severity: "bad",
  },
  {
    type: "large_temperature_spread",
    apiEnum: "INSIGHT_TYPE_ENUM_STATION_LARGE_SPREAD",
    severity: "bad",
  },
  {
    type: "small_temperature_spread",
    apiEnum: "INSIGHT_TYPE_ENUM_STATION_SMALL_SPREAD",
    severity: "good",
  },
  {
    type: "optimal_temperature",
    apiEnum: "INSIGHT_TYPE_ENUM_OPTIMAL_TEMPERATURE",
    severity: "good",
  },
  {
    type: "efficient_heating",
    apiEnum: "INSIGHT_TYPE_ENUM_EFFICIENT_HEATING",
    severity: "good",
  },
  {
    type: "consistent_comfort",
    apiEnum: "INSIGHT_TYPE_ENUM_CONSISTENT_COMFORT",
    severity: "good",
  },
  {
    type: "low_power_peak",
    apiEnum: "INSIGHT_TYPE_ENUM_LOW_POWER_PEAK",
    severity: "good",
  },
  {
    type: "energy_efficient_building",
    apiEnum: "INSIGHT_TYPE_ENUM_ENERGY_EFFICIENT_BUILDING",
    severity: "good",
  },
] as const;

/**
 * Available insight types (derived from single source of truth)
 */
export type InsightType = (typeof INSIGHT_TYPE_DEFINITIONS)[number]["type"];

/**
 * Metadata associated with an insight
 * Different insight types will use different metadata fields
 */
export type InsightMetaData = {
  // For temperature-related insights
  sensor_id?: string;
  average_value?: number;
  deviation_value?: number;

  // For temperature spread insights
  spread_value?: number;
  normal_spread_value?: number;

  // For efficiency insights
  efficiency_percentage?: number;
  energy_savings?: number;

  // For power peak insights
  peak_power?: number;
  city?: string;
  comparison_status?: string;

  // For energy efficiency insights
  energy_usage_kwh_m2?: number;
  percentile?: number;
};

/**
 * Additional insight-specific data
 */
export type InsightData = {
  apartment?: string;
  deviation_value?: number;
  floor?: number | string;
  zone?: string;
  street_address?: string;
};

/**
 * Main insight object structure matching the API response enum strings
 */
export type Insight = {
  insight_type: string;
  date_start: string;
  date_end: string;
  metadata: InsightMetaData;
  insight_data?: InsightData;
};

/**
 * API response structure for live insights endpoint
 */
export type LiveInsightsResponse = {
  flexstation_uid: string;
  collection: Insight[];
};

/**
 * Normalized insight interface for use in components
 * Converts API response to component-friendly format
 */

export type NormalizedInsight = {
  type: InsightType;
  date_start: string;
  date_end: string;
  meta_data: InsightMetaData;
  insight_data?: InsightData;
};

/** Severity per insight type (derived) */
export const INSIGHT_SEVERITY_MAP: Record<InsightType, InsightSeverity> = Object.fromEntries(
  INSIGHT_TYPE_DEFINITIONS.map((d) => [d.type, d.severity])
) as Record<InsightType, InsightSeverity>;

/**
 * API string per internal type (derived).
 */
export const API_INSIGHT_TYPE_ENUM: Record<InsightType, string> = Object.fromEntries(
  INSIGHT_TYPE_DEFINITIONS.map((d) => [d.type, d.apiEnum])
) as Record<InsightType, string>;

/** Map API enum string to internal InsightType (derived) */
const API_TO_INTERNAL_TYPE: Record<string, InsightType> = Object.fromEntries(
  INSIGHT_TYPE_DEFINITIONS.map((d) => [d.apiEnum, d.type])
);

/**
 * Map API enum values to our internal InsightType
 */
export function mapApiInsightType(apiType: string): InsightType | null {
  return API_TO_INTERNAL_TYPE[apiType] ?? null;
}

/**
 * Transform API response to normalized format
 */
export function normalizeInsight(apiInsight: Insight): NormalizedInsight | null {
  const type = mapApiInsightType(apiInsight.insight_type);

  if (!type) {
    logger.warn(`Unknown insight type: ${apiInsight.insight_type}`);
    return null;
  }

  return {
    type,
    date_start: apiInsight.date_start,
    date_end: apiInsight.date_end,
    meta_data: apiInsight.metadata,
    insight_data: apiInsight.insight_data,
  };
}
/**
 * Transform API response collection to normalized format
 */
export function normalizeInsights(apiResponse: LiveInsightsResponse): NormalizedInsight[] {
  return apiResponse.collection
    .map(normalizeInsight)
    .filter((insight): insight is NormalizedInsight => insight !== null);
}

/**
 * Helper function to categorize insights by severity
 */
export function categorizeInsights(insights: NormalizedInsight[]): {
  good: NormalizedInsight[];
  bad: NormalizedInsight[];
} {
  return {
    good: insights.filter((insight) => INSIGHT_SEVERITY_MAP[insight.type] === "good"),
    bad: insights.filter((insight) => INSIGHT_SEVERITY_MAP[insight.type] === "bad"),
  };
}
